import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChefHat,
  Leaf,
  Salad,
  ShieldCheck,
  Users,
  UtensilsCrossed,
  Package,
  FileText,
  HeartHandshake,
} from "lucide-react";
import { useSeo } from "@/components/Seo";

const HERO_IMAGE = "/api/images/66c631f0-899f-4850-8a6c-2befc160a8a8";

const MENU_HIGHLIGHTS = [
  { title: "Paneer specialities", text: "Paneer tikka, achari and pahadi tikka on the starters side, with rich paneer main courses for the buffet." },
  { title: "Dal, rice & biryani", text: "Comforting dals, jeera rice, pulaos and veg biryanis that anchor a traditional vegetarian spread." },
  { title: "Chaat & pasta live counters", text: "Interactive live counters for chaat and pasta that guests of all ages queue up for." },
  { title: "Vegetarian starters & soups", text: "From hara bhara kabab and spring rolls to hot soups and fresh salads for the welcome course." },
  { title: "Sweets & desserts", text: "Gulab jamun, jalebi, halwas, kheer, kulfi and premium desserts to close the meal." },
  { title: "Drinks & mocktails", text: "Welcome drinks, fresh juices and mocktails to greet guests as they arrive." },
];

const WHY_VEG = [
  {
    icon: HeartHandshake,
    title: "Everyone can eat everything",
    text: "With a fully vegetarian spread, every guest — including Jain-leaning and vegetarian-only families — can enjoy the whole menu without asking what's in each dish.",
  },
  {
    icon: ShieldCheck,
    title: "No cross-contamination concerns",
    text: "Because the kitchen is 100% vegetarian, there is no shared preparation with non-vegetarian food at any stage.",
  },
  {
    icon: Leaf,
    title: "Fresh ingredients",
    text: "Dishes are prepared fresh in a hygienic kitchen, planned around your function timings.",
  },
  {
    icon: Salad,
    title: "A genuinely deep veg menu",
    text: "130+ vegetarian dishes across starters, mains, live counters and sweets — vegetarian is the whole menu, not a side section.",
  },
  {
    icon: Users,
    title: "Experienced team",
    text: "A team that has served vegetarian food at weddings, corporate events and family functions across Mumbai.",
  },
  {
    icon: UtensilsCrossed,
    title: "Transparent quotations",
    text: "You get a written, itemised quotation to review and approve before anything is confirmed.",
  },
];

export default function VegetarianCateringMumbai() {
  useSeo({
    title: "Vegetarian Catering in Mumbai | 100% Pure Veg | Tezz Khana",
    description:
      "100% vegetarian catering in Mumbai by Tezz Khana. Pure veg menus for weddings, corporate events, birthdays and family functions across Mumbai, Navi Mumbai and Thane. Request a quote.",
    path: "/vegetarian-catering-mumbai",
    ogTitle: "Vegetarian Catering in Mumbai | Tezz Khana",
    ogDescription:
      "100% pure vegetarian catering in Mumbai for weddings, corporate events and family functions, with flexible menus from Tezz Khana.",
  });

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-vegetarian-catering";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Vegetarian catering",
      name: "Vegetarian Catering in Mumbai",
      description:
        "100% pure vegetarian catering in Mumbai for weddings, corporate events, birthdays and family functions, with flexible menus and custom quotations.",
      url: "https://tezzkhana.com/vegetarian-catering-mumbai",
      areaServed: [
        { "@type": "City", name: "Mumbai" },
        { "@type": "City", name: "Navi Mumbai" },
        { "@type": "City", name: "Thane" },
      ],
      provider: {
        "@type": "FoodEstablishment",
        name: "Tezz Khana Catering Service",
        url: "https://tezzkhana.com",
        servesCuisine: "Indian vegetarian",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Goregaon East, Mumbai",
          addressRegion: "Maharashtra",
          addressCountry: "IN",
        },
      },
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById("ld-vegetarian-catering")?.remove();
    };
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="container-x py-14 sm:py-20" data-testid="vcm-hero">
        <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <span className="kbd-eyebrow">
              <Leaf className="h-3 w-3" /> Tezz Khana Catering Service
            </span>
            <h1 className="mt-4 font-serif-display text-4xl font-semibold leading-tight text-secondary sm:text-5xl lg:text-6xl">
              Vegetarian Catering in Mumbai
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
              100% pure vegetarian catering, thoughtfully prepared with fresh ingredients
              — for every occasion, big or small.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/request-quote"
                data-testid="vcm-hero-quote"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-6 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Request a Custom Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/build-your-menu"
                data-testid="vcm-hero-build"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-border px-6 text-sm font-semibold text-secondary hover:bg-muted"
              >
                <ChefHat className="h-4 w-4" /> Build Your Menu
              </Link>
            </div>
          </div>
          <img
            src={HERO_IMAGE}
            alt="Veg biryani from the Tezz Khana pure vegetarian menu"
            className="h-64 w-full rounded-3xl object-cover shadow-lg sm:h-80 lg:h-96"
            loading="eager"
          />
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="container-x pb-14" data-testid="vcm-intro">
        <div className="max-w-3xl space-y-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
          <p>
            Tezz Khana is a genuinely 100% vegetarian catering service based in Goregaon
            East, Mumbai. There are no non-vegetarian options anywhere on the menu — the
            entire kitchen, every dish and every live counter is pure veg.
          </p>
          <p>
            Whether you&apos;re planning a wedding, an office event, a birthday or a
            family function, browse the{" "}
            <Link to="/menu" className="font-semibold text-primary hover:underline">
              full menu
            </Link>
            , pick a package or build your own selection, and request a customised
            quotation for your event.
          </p>
        </div>
      </section>

      {/* MENU HIGHLIGHTS */}
      <section className="container-x pb-16" data-testid="vcm-menu-highlights">
        <span className="kbd-eyebrow">Menu highlights</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          Our vegetarian menu highlights
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {MENU_HIGHLIGHTS.map((f) => (
            <div key={f.title} className="rounded-2xl border border-border/70 bg-card p-6">
              <h3 className="text-sm font-semibold text-secondary">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.text}</p>
            </div>
          ))}
        </div>
        <Link
          to="/menu"
          data-testid="vcm-menu-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
        >
          Explore the full menu <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* WHY 100% VEGETARIAN */}
      <section className="container-x pb-16" data-testid="vcm-why">
        <span className="kbd-eyebrow">Why pure veg</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          Why choose 100% vegetarian catering
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {WHY_VEG.map((w) => (
            <div key={w.title} className="rounded-2xl border border-border/70 bg-card p-6">
              <w.icon className="h-5 w-5 text-primary" />
              <h3 className="mt-3 text-sm font-semibold text-secondary">{w.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{w.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FLEXIBLE PACKAGES */}
      <section className="container-x pb-16" data-testid="vcm-packages">
        <div className="rounded-3xl border border-border/70 bg-card p-8 sm:p-10">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
            <Package className="h-5 w-5" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-secondary md:text-lg">Flexible packages</h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            If you&apos;d rather start from a ready structure, Tezz Khana offers Silver,
            Gold and Platinum packages, plus a fully custom option. Each package can be
            adjusted to your event and guest count before you receive a quotation — or use
            the{" "}
            <Link to="/build-your-menu" data-testid="vcm-build-link" className="font-semibold text-primary hover:underline">
              menu builder
            </Link>{" "}
            to hand-pick every dish.
          </p>
          <Link
            to="/packages"
            data-testid="vcm-packages-link"
            className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            View packages <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="container-x pb-16" data-testid="vcm-service-area">
        <div className="rounded-3xl border border-border/70 bg-card p-8 sm:p-10">
          <span className="kbd-eyebrow">Service area</span>
          <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
            Pure veg catering across Mumbai, Navi Mumbai and Thane
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Based in Goregaon East, Tezz Khana serves vegetarian catering across Mumbai —
            from South Mumbai to the Western and Central suburbs — as well as Navi Mumbai
            and Thane. Share your venue location while requesting your quote and
            we&apos;ll plan the service around it.
          </p>
        </div>
      </section>

      {/* HOW TO REQUEST A QUOTE */}
      <section className="container-x pb-16" data-testid="vcm-steps">
        <span className="kbd-eyebrow">How it works</span>
        <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
          How to request a vegetarian catering quote
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {[
            { n: "1", t: "Choose your event requirements", d: "Tell us the occasion, date and expected guest count." },
            { n: "2", t: "Select or customise your menu", d: "Pick a package or build your own dish-by-dish selection." },
            { n: "3", t: "Request a quotation", d: "Receive an itemised quote to review, adjust and approve." },
          ].map((s) => (
            <div key={s.n} className="rounded-2xl border border-border/70 bg-card p-6">
              <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-sm font-bold text-primary">{s.n}</span>
              <h3 className="mt-3 text-sm font-semibold text-secondary">{s.t}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
        <Link
          to="/request-quote"
          data-testid="vcm-steps-quote-link"
          className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
        >
          <FileText className="h-4 w-4" /> Go to the quote request form <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* FINAL CTA */}
      <section className="bg-primary py-14 text-primary-foreground sm:py-16" data-testid="vcm-cta">
        <div className="container-x flex flex-col items-center justify-between gap-6 sm:flex-row">
          <div>
            <h2 className="font-serif-display text-2xl font-semibold sm:text-3xl">
              Looking for Vegetarian Catering in Mumbai?
            </h2>
            <p className="mt-2 max-w-xl text-sm text-primary-foreground/80">
              Tell us about your event, and we&apos;ll help you plan a pure vegetarian
              menu.
            </p>
          </div>
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <Link
              to="/request-quote"
              data-testid="vcm-cta-quote"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-accent px-6 text-sm font-semibold text-secondary transition-transform hover:-translate-y-0.5"
            >
              Request Custom Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/build-your-menu"
              data-testid="vcm-cta-build"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-primary-foreground/40 px-6 text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ChefHat className="h-4 w-4" /> Build Your Menu
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
